var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { faChevronDown, faEye } from "@fortawesome/pro-regular-svg-icons";
import { faExclamationCircle, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NotificationUtils } from "@yodlee/common-components/dist/utilities";
import { Button, Col, Form, Icon, Input, Modal, Row, Select, Typography } from "antd";
import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FormFieldConfigs, LabelConfigs } from "../configurations";
var TextArea = Input.TextArea;
export var quillModules = {
    toolbar: [
        [{ 'header': [1, 2, 3, 4, 5, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
    ],
};
export var quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'script', 'indent',
    'color', 'background', 'align',
    'link', 'image'
];
export var getTextLength = function (html) {
    if (!html)
        return 0;
    var tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    var images = tempDiv.querySelectorAll("img");
    images.forEach(function (img) {
        img.remove();
    });
    var styleTags = tempDiv.querySelectorAll("style");
    styleTags.forEach(function (style) {
        style.remove();
    });
    var text = tempDiv.textContent || tempDiv.innerText || "";
    return text.trim().length;
};
var dataURLtoFile = function (dataurl, filename) {
    var _a;
    var arr = dataurl.split(',');
    var mime = ((_a = arr[0].match(/:(.*?);/)) === null || _a === void 0 ? void 0 : _a[1]) || 'image/png';
    var bstr = atob(arr[1]);
    var n = bstr.length;
    var u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};
var getExtensionFromMime = function (mime) {
    var mimeMap = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/gif': 'gif',
        'image/svg+xml': 'svg',
        'image/webp': 'webp'
    };
    return mimeMap[mime] || 'png';
};
export var extractContentFromWrapper = function (html, id) {
    var openingTagRegex = new RegExp("<div\\s+id=[\"']".concat(id, "[\"'][^>]*>"), 'i');
    var match = html.match(openingTagRegex);
    if (!match) {
        return null;
    }
    var content = html.replace(openingTagRegex, '');
    var lastClosingDivIndex = content.lastIndexOf('</div>');
    if (lastClosingDivIndex !== -1) {
        content = content.substring(0, lastClosingDivIndex) + content.substring(lastClosingDivIndex + 6);
    }
    return content;
};
export var extractInlineImages = function (html) {
    if (!html) {
        return { html: '', files: [] };
    }
    var tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    var images = tempDiv.querySelectorAll("img");
    var files = [];
    var imageIndex = 0;
    images.forEach(function (img) {
        var src = img.getAttribute('src') || '';
        if (src.startsWith('data:image/')) {
            var timestamp = Date.now();
            var random = Math.random().toString(36).substring(2, 9);
            var cid = "image".concat(timestamp).concat(random).concat(imageIndex);
            imageIndex++;
            var mimeMatch = src.match(/data:image\/([^;]+)/);
            var mime = mimeMatch ? "image/".concat(mimeMatch[1]) : 'image/png';
            var extension = getExtensionFromMime(mime);
            var imageFile = dataURLtoFile(src, "".concat(cid, ".").concat(extension));
            files.push(imageFile);
            img.setAttribute('src', "cid:".concat(cid));
        }
    });
    return {
        html: tempDiv.innerHTML,
        files: files
    };
};
var RichTextEditor = function (_a) {
    var value = _a.value, onChange = _a.onChange, placeholder = _a.placeholder, _b = _a.disabled, disabled = _b === void 0 ? false : _b, style = _a.style, label = _a.label;
    var _c = useState('editor'), editorMode = _c[0], setEditorMode = _c[1];
    var quillRef = useRef(null);
    var _d = useState(''), editorValue = _d[0], setEditorValue = _d[1];
    var _e = useState(''), rawHtmlValue = _e[0], setRawHtmlValue = _e[1];
    useEffect(function () {
        if (value !== undefined) {
            setEditorValue(value);
            setRawHtmlValue(value);
        }
    }, []);
    var handleModeChange = function (newMode) {
        if (newMode === 'raw') {
            if (editorMode === 'editor' && editorValue && !rawHtmlValue) {
                setRawHtmlValue(editorValue);
            }
            if (onChange) {
                onChange(rawHtmlValue || editorValue || '');
            }
        }
        else {
            if (editorMode === 'raw' && rawHtmlValue && !editorValue) {
                setEditorValue(rawHtmlValue);
            }
            if (onChange) {
                onChange(editorValue || rawHtmlValue || '');
            }
        }
        setEditorMode(newMode);
    };
    var handleEditorChange = function (content) {
        setEditorValue(content);
        if (onChange && editorMode === 'editor') {
            onChange(content);
        }
    };
    var handleRawHtmlChange = function (e) {
        var newValue = e.target.value;
        setRawHtmlValue(newValue);
        if (onChange && editorMode === 'raw') {
            onChange(newValue);
        }
    };
    useEffect(function () {
        if (value !== undefined && value !== editorValue && value !== rawHtmlValue) {
            if (editorMode === 'editor') {
                setEditorValue(value);
            }
            else {
                setRawHtmlValue(value);
            }
        }
    }, [value]);
    return (React.createElement("div", null,
        React.createElement(Row, { type: "flex", align: "middle", gutter: 8, style: { marginBottom: 8 } },
            React.createElement(Col, null, label && React.createElement(Typography.Text, { strong: true }, label)),
            React.createElement(Col, null,
                React.createElement(Select, { value: editorMode, onChange: handleModeChange, disabled: disabled, style: { minWidth: 120 } },
                    React.createElement(Select.Option, { value: "editor" }, "Editor"),
                    React.createElement(Select.Option, { value: "raw" }, "Raw HTML")))),
        editorMode === 'editor' ? (React.createElement(ReactQuill, { ref: quillRef, theme: "snow", modules: quillModules, formats: quillFormats, placeholder: placeholder, value: editorValue || "", onChange: handleEditorChange, readOnly: disabled, style: __assign({ backgroundColor: "white", minHeight: "200px", marginBottom: "42px" }, style) })) : (React.createElement(TextArea, { className: "raw-html-editor", value: rawHtmlValue || "", onChange: handleRawHtmlChange, placeholder: placeholder || "Enter raw HTML content...", disabled: disabled, rows: 10, style: __assign({ fontFamily: 'monospace', fontSize: '12px', marginBottom: "42px" }, style) }))));
};
export default RichTextEditor;
export var RichTextEditorFormField = function (_a) {
    var getFieldDecorator = _a.getFieldDecorator, getFieldValue = _a.getFieldValue, getFieldsValue = _a.getFieldsValue, setFieldsValue = _a.setFieldsValue, _b = _a.required, required = _b === void 0 ? true : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, initialMode = _a.initialMode, initialRichTextValue = _a.initialRichTextValue, initialRawHtmlValue = _a.initialRawHtmlValue, originalDescription = _a.originalDescription;
    var _d = useState(initialMode || 'editor'), editorMode = _d[0], setEditorMode = _d[1];
    var _e = useState(false), showPreviewModal = _e[0], setShowPreviewModal = _e[1];
    var quillRef = useRef(null);
    var descriptionRichTextKey = FormFieldConfigs.DESCRIPTION_RICH_TEXT.key;
    var descriptionRawHtmlKey = 'descriptionRawHtml';
    var MAX_IMAGE_SIZE = 5 * 1024 * 1024;
    useEffect(function () {
        if (initialMode) {
            setEditorMode(initialMode);
            return undefined;
        }
        else {
            var timer_1 = setTimeout(function () {
                var _a, _b;
                var description = getFieldValue(descriptionRichTextKey) || '';
                if (description) {
                    var richTextContent = extractContentFromWrapper(description, 'richText');
                    var rawHtmlContent = extractContentFromWrapper(description, 'rawHtml');
                    if (richTextContent !== null) {
                        setEditorMode('editor');
                        setFieldsValue((_a = {},
                            _a[descriptionRichTextKey] = richTextContent,
                            _a[descriptionRawHtmlKey] = '',
                            _a));
                    }
                    else if (rawHtmlContent !== null) {
                        setEditorMode('raw');
                        setFieldsValue((_b = {},
                            _b[descriptionRichTextKey] = '',
                            _b[descriptionRawHtmlKey] = rawHtmlContent,
                            _b));
                    }
                }
            }, 100);
            return function () { return clearTimeout(timer_1); };
        }
    }, [initialMode]);
    useEffect(function () {
        if (initialMode) {
            var descriptionRichText = getFieldValue(descriptionRichTextKey);
            var descriptionRawHtml = getFieldValue(descriptionRawHtmlKey);
            if (initialMode === 'raw' && descriptionRawHtml && !descriptionRichText) {
            }
            else if (initialMode === 'editor' && descriptionRichText && !descriptionRawHtml) {
            }
        }
    }, [initialMode, getFieldValue(descriptionRichTextKey), getFieldValue(descriptionRawHtmlKey)]);
    var handleModeChange = function (newMode) {
        setEditorMode(newMode);
    };
    useEffect(function () {
        if (editorMode === 'editor' && quillRef.current) {
            var quill_1 = quillRef.current.getEditor();
            if (!quill_1)
                return undefined;
            var toolbar_1 = quill_1.getModule('toolbar');
            if (!toolbar_1)
                return undefined;
            toolbar_1.addHandler('image', function () {
                var input = document.createElement('input');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', 'image/*');
                input.click();
                input.onchange = function () {
                    var _a;
                    var file = (_a = input.files) === null || _a === void 0 ? void 0 : _a[0];
                    if (!file)
                        return;
                    if (file.size > MAX_IMAGE_SIZE) {
                        NotificationUtils.warning("Image size too large", "Image size must be less than 5 MB. Selected image is ".concat((file.size / (1024 * 1024)).toFixed(2), " MB."));
                        return;
                    }
                    if (!file.type.startsWith('image/')) {
                        NotificationUtils.warning("Invalid file type", "Please select a valid image file.");
                        return;
                    }
                    var reader = new FileReader();
                    reader.onload = function () {
                        var range = quill_1.getSelection(true);
                        var index = range ? range.index : quill_1.getLength();
                        quill_1.insertEmbed(index, 'image', reader.result, 'user');
                        quill_1.setSelection(index + 1);
                    };
                    reader.onerror = function () {
                        NotificationUtils.error("Error reading image", "Failed to read the image file. Please try again.");
                    };
                    reader.readAsDataURL(file);
                };
            });
            var handlePaste_1 = function (e) {
                var _a;
                var items = (_a = e.clipboardData) === null || _a === void 0 ? void 0 : _a.items;
                if (!items)
                    return;
                var _loop_1 = function (i) {
                    var item = items[i];
                    if (item.type.startsWith('image/')) {
                        e.preventDefault();
                        var file = item.getAsFile();
                        if (!file)
                            return "continue";
                        if (file.size > MAX_IMAGE_SIZE) {
                            NotificationUtils.warning("Image size too large", "Pasted image size must be less than 5 MB. Image size is ".concat((file.size / (1024 * 1024)).toFixed(2), " MB."));
                            return { value: void 0 };
                        }
                        var reader_1 = new FileReader();
                        reader_1.onload = function () {
                            var range = quill_1.getSelection(true);
                            var index = range ? range.index : quill_1.getLength();
                            quill_1.insertEmbed(index, 'image', reader_1.result, 'user');
                            quill_1.setSelection(index + 1);
                        };
                        reader_1.onerror = function () {
                            NotificationUtils.error("Error reading image", "Failed to read the pasted image. Please try again.");
                        };
                        reader_1.readAsDataURL(file);
                        return { value: void 0 };
                    }
                };
                for (var i = 0; i < items.length; i++) {
                    var state_1 = _loop_1(i);
                    if (typeof state_1 === "object")
                        return state_1.value;
                }
            };
            var timer_2 = setTimeout(function () {
                var _a, _b, _c;
                var editorElement = (_c = (_b = (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.editor) === null || _b === void 0 ? void 0 : _b.container) === null || _c === void 0 ? void 0 : _c.querySelector('.ql-editor');
                if (editorElement) {
                    editorElement.addEventListener('paste', handlePaste_1);
                }
            }, 100);
            return function () {
                var _a, _b, _c;
                clearTimeout(timer_2);
                var editorElement = (_c = (_b = (_a = quillRef.current) === null || _a === void 0 ? void 0 : _a.editor) === null || _b === void 0 ? void 0 : _b.container) === null || _c === void 0 ? void 0 : _c.querySelector('.ql-editor');
                if (editorElement) {
                    editorElement.removeEventListener('paste', handlePaste_1);
                }
            };
        }
        return undefined;
    }, [editorMode]);
    return (React.createElement(Form.Item, { colon: false },
        React.createElement(Row, { type: "flex", align: "middle", gutter: 8, style: { marginBottom: 8 } },
            React.createElement(Col, null,
                React.createElement(Typography.Text, { strong: true }, LabelConfigs.FORMAT)),
            React.createElement(Col, null,
                React.createElement(Select, { suffixIcon: React.createElement(Icon, { component: function () { return React.createElement(FontAwesomeIcon, { icon: faChevronDown }); } }), value: editorMode, onChange: handleModeChange, disabled: disabled, style: { minWidth: 120 } },
                    React.createElement(Select.Option, { value: "editor" }, "Editor"),
                    React.createElement(Select.Option, { value: "raw" }, "Raw HTML"))),
            React.createElement(Col, null,
                React.createElement(Button, { type: "link", onClick: function () { return setShowPreviewModal(true); }, style: { padding: 0 }, disabled: disabled },
                    React.createElement(Icon, { component: function () { return React.createElement(FontAwesomeIcon, { icon: faEye }); } }),
                    " Preview"))),
        editorMode === 'editor' ? (React.createElement(Form.Item, { colon: false }, getFieldDecorator(descriptionRichTextKey, {
            rules: __spreadArray(__spreadArray([], FormFieldConfigs.DESCRIPTION_RICH_TEXT.rules(required), true), [
                {
                    validator: function (rule, value, callback) {
                        var textLength = getTextLength(value);
                        var maxLength = 10000;
                        if (textLength > maxLength) {
                            callback(React.createElement(React.Fragment, null,
                                React.createElement(Icon, { className: "fix-icon-top-1-px color-pink", component: function () { return React.createElement(FontAwesomeIcon, { icon: faExclamationCircle }); } }),
                                React.createElement("span", { style: { paddingRight: "2px" } }),
                                LabelConfigs.CONTENT_BODY,
                                " cannot exceed ",
                                maxLength,
                                " characters."));
                        }
                        else {
                            callback();
                        }
                    }
                }
            ], false)
        })(React.createElement(ReactQuill, { className: "rich-text-editor-wrapper", ref: quillRef, theme: "snow", modules: quillModules, formats: quillFormats, placeholder: FormFieldConfigs.DESCRIPTION_RICH_TEXT.placeholder, readOnly: disabled, style: {
                backgroundColor: "white"
            } })))) : (React.createElement(Form.Item, { colon: false }, getFieldDecorator(descriptionRawHtmlKey, {
            initialValue: initialRawHtmlValue || '',
            rules: required ? [
                {
                    required: true,
                    message: "".concat(LabelConfigs.DESCRIPTION, " is required")
                },
                {
                    validator: function (rule, value, callback) {
                        var textLength = getTextLength(value);
                        var maxLength = 10000;
                        if (textLength > maxLength) {
                            callback(React.createElement(React.Fragment, null,
                                React.createElement(Icon, { className: "fix-icon-top-1-px color-pink", component: function () { return React.createElement(FontAwesomeIcon, { icon: faExclamationCircle }); } }),
                                React.createElement("span", { style: { paddingRight: "2px" } }),
                                LabelConfigs.CONTENT_BODY,
                                " cannot exceed ",
                                maxLength,
                                " characters."));
                        }
                        else {
                            callback();
                        }
                    }
                }
            ] : [
                {
                    validator: function (rule, value, callback) {
                        if (value) {
                            var textLength = getTextLength(value);
                            var maxLength = 10000;
                            if (textLength > maxLength) {
                                callback(React.createElement(React.Fragment, null,
                                    React.createElement(Icon, { className: "fix-icon-top-1-px color-pink", component: function () { return React.createElement(FontAwesomeIcon, { icon: faExclamationCircle }); } }),
                                    React.createElement("span", { style: { paddingRight: "2px" } }),
                                    LabelConfigs.CONTENT_BODY,
                                    " cannot exceed ",
                                    maxLength,
                                    " characters."));
                            }
                            else {
                                callback();
                            }
                        }
                        else {
                            callback();
                        }
                    }
                }
            ]
        })(React.createElement(TextArea, { className: "raw-html-editor", placeholder: "Enter raw HTML content...", disabled: disabled, rows: 10, style: {
                fontFamily: 'monospace',
                fontSize: '12px',
                marginBottom: "42px"
            } })))),
        React.createElement(Modal, { title: "Description Preview", visible: showPreviewModal, onCancel: function () { return setShowPreviewModal(false); }, footer: [
                React.createElement(Button, { key: "close", onClick: function () { return setShowPreviewModal(false); } }, "Close")
            ], width: 800, closeIcon: React.createElement(Icon, { component: function () { return React.createElement(FontAwesomeIcon, { icon: faTimes }); } }) },
            React.createElement("div", { style: { maxHeight: "70vh", overflow: "auto", padding: "16px" } }, (function () {
                var content = '';
                var allFields = getFieldsValue();
                var descriptionRichText = allFields[descriptionRichTextKey] || getFieldValue(descriptionRichTextKey);
                var descriptionRawHtml = allFields[descriptionRawHtmlKey] || getFieldValue(descriptionRawHtmlKey);
                if (!descriptionRichText && initialRichTextValue) {
                    descriptionRichText = initialRichTextValue;
                }
                if (!descriptionRawHtml && initialRawHtmlValue) {
                    descriptionRawHtml = initialRawHtmlValue;
                }
                if (!descriptionRichText && !descriptionRawHtml && originalDescription) {
                    var richTextMatch = extractContentFromWrapper(originalDescription, 'richText');
                    var rawHtmlMatch = extractContentFromWrapper(originalDescription, 'rawHtml');
                    if (editorMode === 'raw' && rawHtmlMatch) {
                        descriptionRawHtml = rawHtmlMatch[1];
                    }
                    else if (editorMode === 'editor' && richTextMatch) {
                        descriptionRichText = richTextMatch[1];
                    }
                    else if (rawHtmlMatch) {
                        descriptionRawHtml = rawHtmlMatch[1];
                    }
                    else if (richTextMatch) {
                        descriptionRichText = richTextMatch[1];
                    }
                }
                content = editorMode === 'raw' ? descriptionRawHtml : descriptionRichText;
                return content && content.trim() ? (React.createElement("div", { dangerouslySetInnerHTML: {
                        __html: content
                    }, style: { maxWidth: "100%" } })) : (React.createElement(Typography.Text, { type: "secondary" }, "No content to preview"));
            })()))));
};
//# sourceMappingURL=rich-text-editor.js.map